import React, { startTransition, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import { useVirtualizer } from "@tanstack/react-virtual";
import { haversineKm } from "./lib/coordinate.js";
import {
  amapNavigationUrl,
  amapWebUrl
} from "./lib/navigation.js";
import {
  matchesScenario,
  matchesType,
  normalizeTeaData,
  sortPlaces,
  TEA_SCENARIOS,
  TEA_SORTS,
  TEA_TYPES,
  toTeaMapCollection
} from "./lib/tea.js";
import {
  createAmapPublicStyle,
  isMapTileError
} from "./map/amap-public.js";

const INITIAL_CENTER = [104.18, 30.48];
const INITIAL_ZOOM = 8.7;
const SCENARIO_COLORS = {
  view: "#1f6f5a",
  yard: "#c96d32",
  old: "#8f3f2e",
  mountain: "#356f8a"
};

async function fetchJson(path) {
  const response = await fetch(path);
  if (!response.ok) throw new Error(`${path}: HTTP ${response.status}`);
  return response.json();
}

function useTeaData() {
  const [state, setState] = useState({
    error: null,
    loading: true,
    metadata: null,
    places: []
  });
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchJson("/data/tea-places.geojson"),
      fetchJson("/data/editorial-shops.json"),
      fetchJson("/data/douyin-discoveries.json")
    ])
      .then(([places, editorial, discoveries]) => {
        if (cancelled) return;
        const normalized = normalizeTeaData({ discoveries, editorial, places });
        setState({
          error: null,
          loading: false,
          metadata: normalized.metadata,
          places: normalized.places
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setState({
          error,
          loading: false,
          metadata: null,
          places: []
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return state;
}

function useMapInstance({ onError, onSelect, onViewport }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const callbacksRef = useRef({ onError, onSelect, onViewport });
  callbacksRef.current = { onError, onSelect, onViewport };
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const map = new maplibregl.Map({
      attributionControl: false,
      center: INITIAL_CENTER,
      container: containerRef.current,
      maxZoom: 18,
      minZoom: 6,
      style: createAmapPublicStyle(),
      zoom: INITIAL_ZOOM
    });
    mapRef.current = map;
    if (import.meta.env.DEV) window.__mapDiscoveryMap = map;
    map.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      "bottom-right"
    );
    map.addControl(
      new maplibregl.AttributionControl({ compact: true }),
      "bottom-left"
    );
    map.on("error", (error) => {
      if (isMapTileError(error)) callbacksRef.current.onError();
    });
    map.on("load", () => {
      map.addSource("tea-places", {
        type: "geojson",
        cluster: true,
        clusterMaxZoom: 11,
        clusterRadius: 44,
        data: { type: "FeatureCollection", features: [] }
      });
      map.addLayer({
        id: "tea-clusters",
        type: "circle",
        source: "tea-places",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#1b6b55",
          "circle-opacity": 0.94,
          "circle-radius": ["step", ["get", "point_count"], 18, 10, 25, 30, 32],
          "circle-stroke-color": "#fffaf0",
          "circle-stroke-width": 3
        }
      });
      map.addLayer({
        id: "tea-cluster-count",
        type: "symbol",
        source: "tea-places",
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-font": ["Open Sans Bold"],
          "text-size": 12
        },
        paint: { "text-color": "#fffaf0" }
      });
      map.addLayer({
        id: "tea-points",
        type: "circle",
        source: "tea-places",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-color": [
            "match",
            ["get", "scenario"],
            "view",
            SCENARIO_COLORS.view,
            "yard",
            SCENARIO_COLORS.yard,
            "old",
            SCENARIO_COLORS.old,
            "mountain",
            SCENARIO_COLORS.mountain,
            "#c79a33"
          ],
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 5, 13, 8, 17, 11],
          "circle-stroke-color": "#fffaf0",
          "circle-stroke-width": 2
        }
      });
      map.addLayer({
        id: "tea-selected",
        type: "circle",
        source: "tea-places",
        filter: ["==", ["get", "mapId"], -1],
        paint: {
          "circle-color": "#ff7043",
          "circle-radius": 13,
          "circle-stroke-color": "#fffaf0",
          "circle-stroke-width": 4
        }
      });
      map.on("click", "tea-clusters", (event) => {
        const feature = map.queryRenderedFeatures(event.point, {
          layers: ["tea-clusters"]
        })[0];
        if (!feature) return;
        map
          .getSource("tea-places")
          .getClusterExpansionZoom(feature.properties.cluster_id)
          .then((zoom) => map.easeTo({ center: feature.geometry.coordinates, zoom }));
      });
      map.on("click", "tea-points", (event) => {
        const feature = event.features?.[0];
        if (feature?.properties?.id) callbacksRef.current.onSelect(feature.properties.id);
      });
      for (const layer of ["tea-clusters", "tea-points"]) {
        map.on("mouseenter", layer, () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", layer, () => {
          map.getCanvas().style.cursor = "";
        });
      }
      map.on("moveend", () => {
        const center = map.getCenter();
        const bounds = map.getBounds();
        callbacksRef.current.onViewport({
          bounds: [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
          center: { lat: center.lat, lng: center.lng },
          zoom: map.getZoom()
        });
      });
      setReady(true);
    });
    return () => {
      if (import.meta.env.DEV) delete window.__mapDiscoveryMap;
      map.remove();
      mapRef.current = null;
    };
  }, []);
  return { containerRef, map: mapRef, ready };
}

function scenarioColor(place) {
  const priority = ["view", "yard", "old", "mountain"];
  const scenario = priority.find((id) => place.scenarios.includes(id));
  return SCENARIO_COLORS[scenario] ?? "#c79a33";
}

function formatCount(value) {
  const count = Number(value);
  if (!Number.isFinite(count) || count <= 0) return "";
  return count > 9999 ? `${(count / 10000).toFixed(1)}万` : String(count);
}

function SearchBar({ onChange, value }) {
  return (
    <label className="search-command">
      <span className="search-icon" aria-hidden="true" />
      <input
        aria-label="搜索茶事地点"
        autoComplete="off"
        onChange={(event) => onChange(event.target.value)}
        placeholder="搜索茶馆、片区或玩法"
        type="search"
        value={value}
      />
      {value ? (
        <button
          aria-label="清空搜索"
          className="search-clear"
          onClick={() => onChange("")}
          type="button"
        >
          ×
        </button>
      ) : null}
    </label>
  );
}

function ScenarioRail({ active, counts, onChange }) {
  return (
    <div className="scenario-rail" role="toolbar" aria-label="周末喝茶场景">
      {TEA_SCENARIOS.map((scenario) => (
        <button
          aria-pressed={active === scenario.id}
          className={`scenario-chip scenario-${scenario.id}`}
          key={scenario.id}
          onClick={() => onChange(scenario.id)}
          type="button"
        >
          <i />
          <span>{scenario.label}</span>
          <strong>{counts[scenario.id] ?? 0}</strong>
        </button>
      ))}
    </div>
  );
}

function QualityToggle({ counts, onChange, value }) {
  return (
    <div className="quality-toggle" role="group" aria-label="数据范围">
      <button
        aria-pressed={value === "rich"}
        onClick={() => onChange("rich")}
        type="button"
      >
        有图有评 <strong>{counts.rich}</strong>
      </button>
      <button
        aria-pressed={value === "all"}
        onClick={() => onChange("all")}
        type="button"
      >
        全部数据库 <strong>{counts.all}</strong>
      </button>
    </div>
  );
}

function TypeRail({ active, counts, onChange }) {
  return (
    <div className="type-rail" role="toolbar" aria-label="周末玩法">
      {TEA_TYPES.map((type) => (
        <button
          aria-pressed={active === type.id}
          className={`type-chip type-${type.id}`}
          key={type.id}
          onClick={() => onChange(type.id)}
          type="button"
        >
          {type.label}
          {type.id === "all" ? null : <strong>{counts[type.id] ?? 0}</strong>}
        </button>
      ))}
    </div>
  );
}

function ListControls({
  counts,
  excludeCity,
  onChange,
  onQualityChange,
  onSortChange,
  quality,
  sort
}) {
  return (
    <div className="list-controls">
      {quality ? (
        <div className="quality-toggle quality-toggle-inline" role="group" aria-label="数据范围">
          <button
            aria-pressed={quality === "rich"}
            onClick={() => onQualityChange("rich")}
            type="button"
          >
            有图有评 <strong>{counts.rich}</strong>
          </button>
          <button
            aria-pressed={quality === "all"}
            onClick={() => onQualityChange("all")}
            type="button"
          >
            全部 <strong>{counts.all}</strong>
          </button>
        </div>
      ) : null}
      <label className="city-filter">
        <input
          checked={excludeCity}
          onChange={(event) => onChange(event.target.checked)}
          type="checkbox"
        />
        <span title="排除蜜雪冰城等城市连锁品牌与商场门店">排除城市连锁</span>
      </label>
      <label className="sort-select">
        <span>排序</span>
        <select onChange={(event) => onSortChange(event.target.value)} value={sort}>
          {TEA_SORTS.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
      </label>
    </div>
  );
}

function TeaList({ onSelect, places, selectedId }) {
  const scrollRef = useRef(null);
  const virtualizer = useVirtualizer({
    count: places.length,
    estimateSize: () => 132,
    getScrollElement: () => scrollRef.current,
    overscan: 8
  });
  return (
    <div className="place-list" ref={scrollRef} role="list">
      <div className="place-list-inner" style={{ height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const place = places[virtualRow.index];
          return (
            <button
              className={`tea-row${selectedId === place.id ? " is-selected" : ""}`}
              key={place.id}
              onClick={() => onSelect(place.id)}
              role="listitem"
              style={{ transform: `translateY(${virtualRow.start}px)` }}
              type="button"
            >
              <span className="tea-row-photo">
                {place.image ? (
                  <img alt="" loading="lazy" referrerPolicy="no-referrer" src={place.image.src} />
                ) : (
                  <span className="tea-row-mark" style={{ background: scenarioColor(place) }}>
                    {place.category.slice(0, 1)}
                  </span>
                )}
                {place.qualityTier === "rich" ? <i className="photo-badge">图·评</i> : null}
              </span>
              <span className="tea-row-copy">
                <span className="tea-row-meta">
                  {place.featured ? "编辑精选" : place.priority}
                  <i>·</i>
                  {place.area}
                </span>
                <strong>{place.name}</strong>
                <small>
                  {place.rating
                    ? `${place.rating} 分`
                    : place.heat
                      ? `抖音 ${formatCount(place.heat)} 赞`
                      : place.duration}
                  {place.reviewCount
                    ? ` · ${formatCount(place.reviewCount)} 条评价`
                    : ""}
                  {!place.reviewCount && place.price ? ` · ¥${place.price}/人` : ""}
                </small>
                <small className="tea-row-sub">
                  {place.category}
                  {place.duration ? ` · ${place.duration}` : ""}
                </small>
              </span>
              <span className="place-arrow" aria-hidden="true">→</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RatingStars({ rating }) {
  const normalized = Math.max(0, Math.min(5, Number(rating) || 0));
  return (
    <span className="rating-stars" aria-label={`${normalized} 分`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <i className={star <= Math.round(normalized) ? "is-filled" : ""} key={star}>★</i>
      ))}
    </span>
  );
}

function PhotoWall({ images, onOpenImage }) {
  if (!images.length) return null;
  return (
    <section className="photo-wall">
      <header>
        <p>真实图片</p>
        <h2>先去现场看一眼</h2>
        <span>{images.length} 张</span>
      </header>
      <div className="photo-strip">
        {images.map((image, index) => (
          <button
            aria-label={`查看现场图片 ${index + 1}`}
            key={image}
            onClick={() => onOpenImage(image)}
            type="button"
          >
            <img alt="" loading="lazy" referrerPolicy="no-referrer" src={image} />
          </button>
        ))}
      </div>
    </section>
  );
}

function ReviewsSection({ loading, onOpenImage, reviews }) {
  return (
    <section className="reviews-section">
      <header>
        <div>
          <p>用户原评论</p>
          <h2>大家真实坐过之后怎么说</h2>
        </div>
        <span>{reviews.length} 条</span>
      </header>
      {loading ? <p className="review-state">正在读取公开评论...</p> : null}
      {!loading && reviews.length === 0 ? (
        <p className="review-state">该店当前没有可稳定读取的公开评论。</p>
      ) : null}
      <div className="review-stack">
        {reviews.map((review) => (
          <article className="review-card" key={review.id}>
            <div className="review-head">
              <span className="review-avatar">点</span>
              <div>
                <strong>{review.author}</strong>
                <small>{review.date}</small>
              </div>
              <RatingStars rating={review.rating} />
            </div>
            {review.price ? <p className="review-price">{review.price}</p> : null}
            <p className="review-text">{review.text}</p>
            {review.images?.length ? (
              <div className={`review-images review-images-${Math.min(review.images.length, 3)}`}>
                {review.images.slice(0, 3).map((image, index) => (
                  <button
                    aria-label={`查看评论图片 ${index + 1}`}
                    key={image}
                    onClick={() => onOpenImage(image)}
                    type="button"
                  >
                    <img alt="" loading="lazy" referrerPolicy="no-referrer" src={image} />
                  </button>
                ))}
              </div>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}

function VideoCluesSection({ clues }) {
  if (!clues?.length) return null;
  return (
    <section className="video-clues">
      <header>
        <p>抖音公开现场线索</p>
        <h2>视频里怎么玩</h2>
      </header>
      <div className="video-clue-list">
        {clues.map((clue) => (
          <article key={clue.id}>
            {clue.image?.src ? (
              <img alt="" loading="lazy" referrerPolicy="no-referrer" src={clue.image.src} />
            ) : null}
            <div>
              <strong>{clue.mood || "现场视频"}</strong>
              <p>{clue.summary}</p>
              <small>
                {clue.stats?.likes ? `${clue.stats.likes} 赞` : ""}
                {clue.stats?.comments ? ` · ${clue.stats.comments} 评论` : ""}
              </small>
              {clue.sourceUrl ? (
                <a href={clue.sourceUrl} rel="noreferrer" target="_blank">打开抖音视频</a>
              ) : null}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function TeaDetail({ onClose, place, reviews = [], reviewsLoading = false }) {
  const [activeImage, setActiveImage] = useState(null);
  if (!place) return null;
  const galleryImages = [
    ...new Set([
      ...(place.image?.src ? [place.image.src] : []),
      ...reviews.flatMap((review) => review.images ?? [])
    ])
  ].slice(0, 12);
  const sourceLabel = place.featured
    ? "大众点评公开页与抖音公开线索"
    : place.dataTier === "osm"
      ? "OpenStreetMap 公共点位"
      : place.dataTier === "dianping-index"
        ? "大众点评公开搜索结果"
        : place.dataTier === "douyin"
          ? "抖音公开视频与评论线索"
          : "开放地图与公开文章整理";
  return (
    <section className="tea-detail">
      <button className="detail-close" onClick={onClose} type="button">返回清单</button>
      {place.image ? (
        <img
          alt={place.image.alt ?? place.name}
          className="detail-image"
          referrerPolicy="no-referrer"
          src={place.image.src}
        />
      ) : null}
      <p className="detail-kicker">{place.area} · {place.category}</p>
      <h1>{place.name}</h1>
      <p className="detail-deck">{place.teaStyle}</p>
      {place.rating || place.price || place.reviewCount ? (
        <div className="detail-metrics">
          {place.rating ? <div><strong>{place.rating}</strong><span>评分</span></div> : null}
          {place.reviewCount ? <div><strong>{place.reviewCount}</strong><span>评价</span></div> : null}
          {place.price ? <div><strong>¥{place.price}</strong><span>人均</span></div> : null}
        </div>
      ) : null}
      <PhotoWall images={galleryImages} onOpenImage={setActiveImage} />
      {place.qualityTier === "rich" || reviewsLoading || reviews.length > 0 ? (
        <ReviewsSection
          loading={reviewsLoading}
          onOpenImage={setActiveImage}
          reviews={reviews}
        />
      ) : null}
      <VideoCluesSection clues={place.videoClues} />
      <p className="detail-summary">{place.description}</p>
      {place.tags?.length ? (
        <div className="detail-tags">
          {place.tags.map((tag) => <span key={tag}>{tag}</span>)}
        </div>
      ) : null}
      {place.reviewThemes?.length ? (
        <section className="detail-section">
          <h2>大家在评论里关心什么</h2>
          <div className="theme-cloud">
            {place.reviewThemes.slice(0, 8).map((theme) => (
              <span key={theme.label}>
                {theme.label}
                {theme.count ? <strong>{theme.count}</strong> : null}
              </span>
            ))}
          </div>
        </section>
      ) : null}
      {place.details?.length ? (
        <section className="detail-section">
          <h2>到店前先知道</h2>
          <dl className="detail-facts">
            {place.details.map((detail) => (
              <div key={`${detail.label}:${detail.value}`}>
                <dt>{detail.label}</dt>
                <dd>{detail.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}
      {place.tip ? <p className="detail-tip">{place.tip}</p> : null}
      <section className="detail-section">
        <h2>位置与数据边界</h2>
        <p className="precision-note">{place.coordinatePrecision}</p>
        {place.address ? <p className="detail-address">{place.address}</p> : null}
        {place.hours ? <p className="detail-address">营业口径：{place.hours}</p> : null}
      </section>
      <div className="detail-actions">
        <a href={amapNavigationUrl(place)}>高德地图导航</a>
        <a href={amapWebUrl(place)} rel="noreferrer" target="_blank">高德查看</a>
        {place.source ? (
          <a href={place.source} rel="noreferrer" target="_blank">打开原始来源</a>
        ) : null}
        {place.osmUrl ? <a href={place.osmUrl} rel="noreferrer" target="_blank">OSM 位置</a> : null}
      </div>
      <p className="source-boundary">数据来源：{sourceLabel}</p>
      {activeImage ? (
        <div
          aria-label="图片预览"
          aria-modal="true"
          className="review-lightbox"
          onClick={() => setActiveImage(null)}
          role="dialog"
        >
          <button aria-label="关闭图片" onClick={() => setActiveImage(null)} type="button">×</button>
          <img alt="用户评论图片" referrerPolicy="no-referrer" src={activeImage} />
        </div>
      ) : null}
    </section>
  );
}

function DesktopPanel({
  counts,
  excludeCity,
  onExcludeCityChange,
  onQualityChange,
  onScenarioChange,
  onSelect,
  onSortChange,
  onTypeChange,
  onQueryChange,
  mappableCount,
  places,
  query,
  quality,
  scenario,
  sort,
  selectedId,
  totalCount,
  type
}) {
  return (
    <aside className="desktop-panel">
      <header className="tea-hero">
        <p>CHENGDU WEEKEND TEA</p>
        <h1>周末，把成都泡进一壶茶</h1>
        <span>龙泉山看日落，城南小院围炉，老场镇喝盖碗。</span>
        <div className="hero-stats">
          <strong>{totalCount}</strong><span>个地点</span>
          <strong>{counts.areas}</strong><span>个片区</span>
          <strong>{mappableCount}</strong><span>可落图</span>
        </div>
      </header>
      <SearchBar onChange={onQueryChange} value={query} />
      <ScenarioRail active={scenario} counts={counts} onChange={onScenarioChange} />
      <TypeRail active={type} counts={counts} onChange={onTypeChange} />
      <QualityToggle counts={counts} onChange={onQualityChange} value={quality} />
      <ListControls
        excludeCity={excludeCity}
        onChange={onExcludeCityChange}
        onSortChange={onSortChange}
        sort={sort}
      />
      <div className="panel-heading">
        <strong>周末茶事清单</strong>
        <span>{places.length} 条</span>
      </div>
      <TeaList onSelect={onSelect} places={places} selectedId={selectedId} />
    </aside>
  );
}

function DesktopDetail({ onClose, place, reviews, reviewsLoading }) {
  return place ? (
    <aside className="desktop-detail">
      <TeaDetail
        onClose={onClose}
        place={place}
        reviews={reviews}
        reviewsLoading={reviewsLoading}
      />
    </aside>
  ) : null;
}

function MobileSheet({
  counts,
  excludeCity,
  mappableCount,
  onExcludeCityChange,
  onQueryChange,
  onQualityChange,
  onScenarioChange,
  onSelect,
  onSortChange,
  onTypeChange,
  places,
  query,
  quality,
  reviews,
  reviewsLoading,
  scenario,
  selectedPlace,
  setSelectedId,
  sort,
  totalCount,
  type
}) {
  const [mode, setMode] = useState("half");
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const sheetRef = useRef(null);
  const [dragHeight, setDragHeight] = useState(null);
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    if (selectedPlace) setMode("full");
  }, [selectedPlace]);

  const sheetSnapPoints = () => {
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    return {
      full: viewportHeight * 0.9,
      half: viewportHeight * 0.62,
      peek: viewportHeight * 0.31
    };
  };

  const clampSheetHeight = (height) => {
    const points = sheetSnapPoints();
    return Math.min(points.full, Math.max(points.peek, height));
  };

  const toggleSheet = () => {
    if (mode === "full") {
      setMode("half");
    } else {
      setMode("full");
    }
  };

  const onPointerDown = (event) => {
    dragRef.current = {
      lastY: event.clientY,
      lastTime: performance.now(),
      mode,
      moved: false,
      pointerId: event.pointerId,
      startHeight:
        sheetRef.current?.getBoundingClientRect().height ??
        sheetSnapPoints()[mode],
      startTime: performance.now(),
      startY: event.clientY
    };
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (drag?.pointerId !== event.pointerId) return;
    if (Math.abs(event.clientY - drag.startY) > 5) drag.moved = true;
    drag.lastY = event.clientY;
    drag.lastTime = performance.now();
    drag.lastHeight = clampSheetHeight(
      drag.startHeight + drag.startY - event.clientY
    );
    setDragHeight(drag.lastHeight);
  };

  const finishDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const points = sheetSnapPoints();
    const order = ["peek", "half", "full"];
    const elapsed = Math.max(1, performance.now() - drag.startTime);
    const velocity = (drag.startY - drag.lastY) / elapsed;
    let nextMode;
    if (velocity > 0.45) {
      nextMode = order[Math.min(order.indexOf(drag.mode) + 1, order.length - 1)];
    } else if (velocity < -0.45) {
      nextMode = order[Math.max(order.indexOf(drag.mode) - 1, 0)];
    } else {
      const currentHeight = drag.lastHeight ?? drag.startHeight;
      nextMode = order.reduce((nearest, candidate) =>
        Math.abs(points[candidate] - currentHeight) <
        Math.abs(points[nearest] - currentHeight)
          ? candidate
          : nearest
      );
    }
    suppressClickRef.current = drag.moved;
    dragRef.current = null;
    setDragHeight(null);
    setDragging(false);
    setMode(nextMode);
    if (drag.moved) {
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    }
  };

  return (
    <aside
      className={`mobile-sheet sheet-${mode}${dragging ? " is-dragging" : ""}`}
      ref={sheetRef}
      style={dragHeight ? { height: `${dragHeight}px` } : undefined}
    >
      <button
        aria-expanded={mode !== "peek"}
        aria-label="调整茶事面板高度"
        className="sheet-handle"
        onClick={() => {
          if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
          }
          toggleSheet();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleSheet();
          }
        }}
        onLostPointerCapture={() => {
          if (dragRef.current) {
            dragRef.current = null;
            setDragHeight(null);
            setDragging(false);
          }
        }}
        onPointerCancel={finishDrag}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishDrag}
        type="button"
      >
        <span />
      </button>
      {selectedPlace ? (
        <TeaDetail
          onClose={() => {
            setSelectedId(null);
            setMode("half");
          }}
          place={selectedPlace}
          reviews={reviews}
          reviewsLoading={reviewsLoading}
        />
      ) : (
        mode === "peek" ? (
          <div className="sheet-collapsed">
            <div>
              <span>{totalCount} 个地点</span>
              <strong>周末茶事清单已收起</strong>
            </div>
            <button onClick={() => setMode("half")} type="button">展开清单</button>
          </div>
        ) : (
          <>
          <div className="sheet-heading">
            <div>
              <span>{totalCount} 个地点</span>
              <strong>周末茶事清单</strong>
            </div>
            <small>{mappableCount} 个地图点位</small>
          </div>
          <SearchBar onChange={onQueryChange} value={query} />
          <ScenarioRail active={scenario} counts={counts} onChange={onScenarioChange} />
          <TypeRail active={type} counts={counts} onChange={onTypeChange} />
          <ListControls
            counts={counts}
            excludeCity={excludeCity}
            onChange={onExcludeCityChange}
            onQualityChange={onQualityChange}
            onSortChange={onSortChange}
            quality={quality}
            sort={sort}
          />
          <TeaList onSelect={onSelect} places={places} selectedId={null} />
          </>
        )
      )}
    </aside>
  );
}

function App() {
  const { error, loading, places } = useTeaData();
  const [scenario, setScenario] = useState("all");
  const [type, setType] = useState("all");
  const [quality, setQuality] = useState("rich");
  const [sort, setSort] = useState("popular");
  const [excludeCity, setExcludeCity] = useState(true);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [reviewState, setReviewState] = useState({
    loading: false,
    reviews: [],
    shopId: null
  });
  const [viewport, setViewport] = useState(null);
  const [basemapError, setBasemapError] = useState(false);
  const deferredQuery = useDeferredValue(query.trim().toLocaleLowerCase("zh-CN"));

  const filteredPlaces = useMemo(
    () =>
      places.filter(
        (place) =>
          matchesScenario(place, scenario) &&
          matchesType(place, type) &&
          (quality === "all" || place.qualityTier === "rich") &&
          (!excludeCity || !place.cityStyle) &&
          (!deferredQuery || place.searchText.includes(deferredQuery))
      ),
    [deferredQuery, excludeCity, places, quality, scenario, type]
  );
  const mapPlaces = useMemo(
    () =>
      places.filter(
        (place) =>
          place.mappable &&
          matchesScenario(place, scenario) &&
          matchesType(place, type) &&
          (!excludeCity || !place.cityStyle) &&
          (!deferredQuery || place.searchText.includes(deferredQuery))
      ),
    [deferredQuery, excludeCity, places, scenario, type]
  );
  const visiblePlaces = useMemo(() => {
    const inViewport = filteredPlaces.filter((place) => {
      if (!place.mappable || !viewport) return true;
      const [west, south, east, north] = viewport.bounds;
      return place.lng >= west && place.lng <= east && place.lat >= south && place.lat <= north;
    });
    return sortPlaces(inViewport, sort, (place) => {
      if (!viewport?.center || !place.mappable) return Number.POSITIVE_INFINITY;
      return haversineKm(viewport.center, place);
    });
  }, [filteredPlaces, sort, viewport]);
  const scopedPlaces = useMemo(
    () => places.filter((place) => !excludeCity || !place.cityStyle),
    [excludeCity, places]
  );
  const countBase = useMemo(
    () =>
      scopedPlaces.filter(
        (place) => quality === "all" || place.qualityTier === "rich"
      ),
    [quality, scopedPlaces]
  );
  const counts = useMemo(() => {
    const result = {
      all: scopedPlaces.length,
      areas: new Set(countBase.map((place) => place.area)).size,
      mappable: countBase.filter((place) => place.mappable).length
    };
    for (const option of TEA_SCENARIOS) {
      if (option.id === "all") continue;
      result[option.id] = countBase.filter((place) =>
        place.scenarios.includes(option.id)
      ).length;
    }
    result.rich = scopedPlaces.filter(
      (place) => place.qualityTier === "rich"
    ).length;
    for (const option of TEA_TYPES) {
      if (option.id === "all") continue;
      result[option.id] = countBase.filter((place) =>
        (place.types ?? []).includes(option.id)
      ).length;
    }
    return result;
  }, [countBase, scopedPlaces]);
  const selectedPlace = places.find((place) => place.id === selectedId) ?? null;
  const mappableCount = useMemo(
    () => places.filter((place) => place.mappable).length,
    [places]
  );
  const selectPlace = (id) => startTransition(() => setSelectedId(id));
  const { containerRef, map, ready } = useMapInstance({
    onError: () => setBasemapError(true),
    onSelect: selectPlace,
    onViewport: setViewport
  });

  useEffect(() => {
    const instance = map.current;
    if (!ready || !instance?.getSource("tea-places")) return;
    instance
      .getSource("tea-places")
      .setData(toTeaMapCollection(mapPlaces));
  }, [map, mapPlaces, ready]);

  useEffect(() => {
    const instance = map.current;
    if (!ready || !instance) return;
    instance.setFilter("tea-selected", [
      "==",
      ["get", "mapId"],
      selectedPlace?.mapId ?? -1
    ]);
    if (!selectedPlace?.mappable) return;
    instance.flyTo({
      center: [selectedPlace.displayLng, selectedPlace.displayLat],
      duration: 900,
      essential: true,
      offset: window.innerWidth <= 860 ? [0, -130] : [-160, 0],
      zoom: Math.max(instance.getZoom(), 12.5)
    });
  }, [map, ready, selectedPlace]);

  useEffect(() => {
    if (!selectedId) {
      setReviewState({ loading: false, reviews: [], shopId: null });
      return undefined;
    }
    const place = places.find((candidate) => candidate.id === selectedId);
    if (!place?.reviewSnapshot) {
      setReviewState({ loading: false, reviews: [], shopId: selectedId });
      return undefined;
    }
    let cancelled = false;
    setReviewState({ loading: true, reviews: [], shopId: selectedId });
    fetchJson(`/data/reviews/${selectedId}.json`)
      .then((data) => {
        if (!cancelled) {
          setReviewState({
            loading: false,
            reviews: data.reviews ?? [],
            shopId: selectedId
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReviewState({ loading: false, reviews: [], shopId: selectedId });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [places, selectedId]);

  return (
    <main className="app-shell">
      <section className="map-stage">
        <div className="map-canvas" ref={containerRef} />
        <div className="map-title">
          <span>成都周末茶事地图</span>
          <strong>{selectedPlace?.name ?? "今天去哪里坐一会儿"}</strong>
        </div>
        <div className="map-legend" aria-hidden="true">
          <span><i style={{ background: SCENARIO_COLORS.view }} />看风景</span>
          <span><i style={{ background: SCENARIO_COLORS.yard }} />找院子</span>
          <span><i style={{ background: SCENARIO_COLORS.old }} />老茶馆</span>
          <span><i style={{ background: SCENARIO_COLORS.mountain }} />上山喝茶</span>
        </div>
      </section>
      {basemapError ? (
        <div className="basemap-warning" role="status">
          高德公共底图暂不可用，茶事清单和地点详情仍可继续使用。
        </div>
      ) : null}
      {loading ? <div className="app-status">正在整理周末茶事清单...</div> : null}
      {error ? <div className="app-status app-status-error">{error.message}</div> : null}
      <DesktopPanel
        counts={counts}
        excludeCity={excludeCity}
        onExcludeCityChange={(value) => startTransition(() => setExcludeCity(value))}
        onQueryChange={setQuery}
        onScenarioChange={(value) => startTransition(() => setScenario(value))}
        onQualityChange={(value) => startTransition(() => setQuality(value))}
        onSelect={selectPlace}
        onSortChange={(value) => startTransition(() => setSort(value))}
        onTypeChange={(value) => startTransition(() => setType(value))}
        mappableCount={mappableCount}
        places={visiblePlaces}
        query={query}
        quality={quality}
        scenario={scenario}
        sort={sort}
        selectedId={selectedId}
        totalCount={countBase.length}
        type={type}
      />
      <DesktopDetail
        onClose={() => setSelectedId(null)}
        place={selectedPlace}
        reviews={reviewState.shopId === selectedId ? reviewState.reviews : []}
        reviewsLoading={reviewState.loading && reviewState.shopId === selectedId}
      />
      <MobileSheet
        counts={counts}
        excludeCity={excludeCity}
        onExcludeCityChange={(value) => startTransition(() => setExcludeCity(value))}
        onQueryChange={setQuery}
        onScenarioChange={(value) => startTransition(() => setScenario(value))}
        onQualityChange={(value) => startTransition(() => setQuality(value))}
        onSelect={selectPlace}
        onSortChange={(value) => startTransition(() => setSort(value))}
        onTypeChange={(value) => startTransition(() => setType(value))}
        mappableCount={mappableCount}
        places={visiblePlaces}
        query={query}
        quality={quality}
        reviews={reviewState.shopId === selectedId ? reviewState.reviews : []}
        reviewsLoading={reviewState.loading && reviewState.shopId === selectedId}
        scenario={scenario}
        selectedPlace={selectedPlace}
        setSelectedId={setSelectedId}
        sort={sort}
        totalCount={countBase.length}
        type={type}
      />
    </main>
  );
}

export default App;
